// Stencil
import {
  Component,
  Host,
  h,
  Prop,
  Element,
  Event,
  EventEmitter,
  State,
  Watch,
  Method
} from "@stencil/core";
// Other Libraries
import { MercuryBundles } from "@genexus/mercury";
// Custom Imports
import { Locale } from "../../common/locale";
import {
  mapNavigationDataToTreeView,
  STATUS_REGEXP_KEY,
  CAPTION_REGEXP_KEY
} from "./helpers";
import {
  ChEditCustomEvent,
  TreeViewItemModelExtended,
  TreeViewModel
} from "@genexus/chameleon-controls-library";
import { CheckedItemsInfo } from "../_helpers/list-selector/list-selector";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/tab",
  "components/tooltip",
  "components/tree-view",
  "utils/form--full",
  "utils/layout",
  "utils/typography",
  "utils/spacing",
  "chameleon/scrollbar"
];

@Component({
  tag: "gx-ide-navigation-report",
  styleUrl: "navigation-report.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/navigation-report"]
})
export class GxIdeNavigationReport {
  /**
   * The component hard-coded strings translations.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;

  #items: NavigationData[] = [];

  @Element() el: HTMLGxIdeNavigationReportElement;
  @State() reportTreeItems: TreeViewModel = [];
  @State() navigationItemsStatusRegExpString: string = "";
  @Watch("navigationItemsStatusRegExpString")
  navigationItemsStatusRegExpStringChanged() {
    this.#combinedRegExp();
  }
  @State() navigationItemsCaptionRegExpString: string = "";
  @Watch("navigationItemsCaptionRegExpString")
  navigationItemsCaptionRegExpStringChanged() {
    this.#combinedRegExp();
  }
  @State() navigationItemsRegExp: RegExp;
  @State() confirmed = false;
  @State() headerState: "none" | "create" | "reorganize" = "none";

  @State() statusInfo: StatusInfo = {
    error: {
      display: true,
      number: 0
    },
    warning: {
      display: true,
      number: 0
    },
    success: {
      display: true,
      number: 0
    }
  };
  @State() someStatusVisible: boolean;

  /**
   * The header data to display on the header.
   */
  @Prop() readonly headerData: HeaderData;

  /**
   * Callback invoked when user confirms data base reorganization
   */
  @Prop() readonly confirmCallback: ConfirmCallback;

  /**
   * Callback invoked when user cancels reorganization
   */
  @Prop() readonly cancelCallback: CancelCallback;

  /**
   * Callback to be invoked when the user closes the dialog
   */
  @Prop() readonly closeCallback: CloseCallback;

  /**
   * Callback invoked when a list item is selected
   */
  @Prop() readonly itemSelectedCallback: ItemSelectedCallback;

  /**
   * @description Gets fired when the component has rendered for the first time.
   */
  @Event() componentDidRenderEvent: EventEmitter<string>;

  /**
   * Add a new item
   */
  @Method()
  async addItem(item: NavigationData) {
    this.#handleChangedItems(this.#items.concat([item]));
  }

  /**
   * Clear items
   */
  @Method()
  async clearItems() {
    this.#handleChangedItems([]);
  }

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
  }

  #combinedRegExp = () => {
    const statusPattern = this.navigationItemsStatusRegExpString || "";
    const captionPattern = this.navigationItemsCaptionRegExpString || "";

    // Create a RegExp that combines the values from the buttons statues and the filer value.
    const combinedPattern = `${statusPattern && `(${statusPattern})`}${
      statusPattern && captionPattern ? ".*" : ""
    }${captionPattern && `(${captionPattern})`}`;

    this.navigationItemsRegExp = new RegExp(combinedPattern, "i");
  };

  #itemsSelectionChangedHandler = (
    event: CustomEvent<TreeViewItemModelExtended[]>
  ) => {
    const selectedItemId = event.detail[0]?.item.id;
    if (selectedItemId) {
      this.itemSelectedCallback(selectedItemId);
    }
  };

  #sortItemsCallback = (subModel: TreeViewModel) => {
    return subModel.sort((a, b) => a.caption.localeCompare(b.caption));
  };

  #statusButtonsSelectionChangedHandler = (
    event: CustomEvent<CheckedItemsInfo>
  ) => {
    const activeStates = event.detail;
    const statuses: Status[] = [];

    const errorIndex = activeStates.findIndex(
      state => state.itemId === "errors"
    );
    const warningIndex = activeStates.findIndex(
      state => state.itemId === "warnings"
    );
    const successIndex = activeStates.findIndex(
      state => state.itemId === "successes"
    );

    if (errorIndex !== -1) {
      statuses.push("error");
    }
    if (warningIndex !== -1) {
      statuses.push("warning");
    }
    if (successIndex !== -1) {
      statuses.push("success");
    }

    if (statuses.length) {
      const statusOptions = statuses.join("|");
      this.navigationItemsStatusRegExpString = `"${STATUS_REGEXP_KEY}":"(${statusOptions})"`;
    } else {
      this.navigationItemsStatusRegExpString = `"${STATUS_REGEXP_KEY}":""`;
    }

    // Update statusInfo for the status-buttons
    this.statusInfo = {
      error: {
        display: errorIndex !== -1,
        number: this.statusInfo.error.number
      },
      warning: {
        display: warningIndex !== -1,
        number: this.statusInfo.warning.number
      },
      success: {
        display: successIndex !== -1,
        number: this.statusInfo.success.number
      }
    };
  };

  #patternInputHandler = (event: ChEditCustomEvent<string> | InputEvent) => {
    const patternFilterValue = event.detail as string;
    this.navigationItemsCaptionRegExpString = `"${CAPTION_REGEXP_KEY}":".*${patternFilterValue}.*"`;
  };

  #handleChangedItems = (newItems: NavigationData[]) => {
    this.#items = newItems;
    this.reportTreeItems = mapNavigationDataToTreeView(newItems);
    let errorsCount: number = 0;
    let warningsCount: number = 0;
    let successCount: number = 0;
    this.reportTreeItems.forEach(item => {
      const metadataObject = JSON.parse(item.metadata);
      let statusValue;
      if (STATUS_REGEXP_KEY in metadataObject) {
        statusValue = metadataObject[STATUS_REGEXP_KEY] as Status;
      }

      if (statusValue && statusValue === "error") {
        errorsCount++;
      } else if (statusValue && statusValue === "warning") {
        warningsCount++;
      } else if (statusValue && statusValue === "success") {
        successCount++;
      }
    });

    this.statusInfo["error"].number = errorsCount;
    this.statusInfo["warning"].number = warningsCount;
    this.statusInfo["success"].number = successCount;
  };

  render() {
    return (
      <Host
        class={{
          "widget": true,
          "display-header-type": !!this.headerData
        }}
      >
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <section class="section">
          {this.headerData && (
            <header class="header dialog-header-with-border spacing-body-inline spacing-body-block-start">
              <div class="header__description">
                <h3 class="subtitle-regular-s">{this.headerData.title}</h3>
                <p class="body-regular-s header__description-p">
                  {this.headerData.description}
                </p>
              </div>
              <div class="header__actions">
                <div class="buttons-spacer header__actions">
                  {this.headerData.buttons?.map(button => {
                    const buttonIcon = button.icon;
                    const buttonType: headerButtonType =
                      button.type ?? "button-primary";
                    return (
                      <button
                        class={{
                          [buttonType]: true,
                          "button-icon-and-text": !!buttonIcon
                        }}
                        onClick={button.callback}
                      >
                        {buttonIcon && (
                          <ch-image class="icon-m" src={buttonIcon}></ch-image>
                        )}
                        {button.caption}
                      </button>
                    );
                  })}
                </div>
              </div>
            </header>
          )}

          <section class="aside field-group">
            <header class="aside__header spacing-body-inline spacing-body-block-start">
              <div class="field field-block">
                <label class="label" htmlFor="pattern">
                  {this.#componentLocale.aside.filterLabel}
                </label>
                <ch-edit
                  id="pattern"
                  class="input"
                  placeholder={this.#componentLocale.aside.filterPlaceholder}
                  onInput={this.#patternInputHandler}
                ></ch-edit>
              </div>
            </header>
            <ch-tree-view-render
              class="tree-view"
              show-lines="last"
              filter={this.navigationItemsRegExp}
              filterType="metadata"
              model={this.reportTreeItems}
              onSelectedItemsChange={this.#itemsSelectionChangedHandler}
              sortItemsCallback={this.#sortItemsCallback}
              filterOptions={{
                autoExpand: true,
                hideMatchesAndShowNonMatches: false
              }}
            ></ch-tree-view-render>
          </section>
          <section class="main spacing-body">
            <slot></slot>
          </section>
          <footer class="footer dialog-footer-with-border dialog-footer-start spacing-body-inline spacing-body-block-end">
            <gx-ide-status-buttons
              errors={this.statusInfo.error.number}
              warnings={this.statusInfo.warning.number}
              successes={this.statusInfo.success.number}
              hideMessage
              compact
              onSelectionChanged={this.#statusButtonsSelectionChangedHandler}
            ></gx-ide-status-buttons>
          </footer>
        </section>
      </Host>
    );
  }
}

export type NavigationData = {
  id: string;
  result: Status;
  icon: string;
  name: string;
};

export type ConfirmCallback = () => Promise<void>;
export type CancelCallback = () => Promise<void>;
export type CloseCallback = () => Promise<void>;
export type ItemSelectedCallback = (id: string) => Promise<void>;

type StatusInfo = {
  error: {
    display: boolean;
    number: number;
  };
  warning: {
    display: boolean;
    number: number;
  };
  success: {
    display: boolean;
    number: number;
  };
};

export type HeaderData = {
  title: string;
  description: string;
  buttons: HeaderButtonData[];
};

export type HeaderButtonData = {
  caption: string;
  icon?: string;
  type?: headerButtonType;
  callback: () => Promise<void>;
};

export type headerButtonType = "button-primary" | "button-secondary";

export type Status = "success" | "error" | "warning";
